import axios from 'axios';
import { isAndroidWebView } from '@/utils/webviewDetect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Only return true when a real Google Sign-In bridge is actually present.
// The webtonative SDK script loaded in index.html stubs window.WTN.socialLogin.google.login
// in every browser environment. Without the isAndroidWebView guard, plain Chrome on Android
// matches the stub and routes to the native path where the stub never calls back
// → 90-second timeout → "Google did not respond".
// isAndroidWebView (UA: "; wv)") is true only inside the actual WTN WebView shell.
export const isNativeGoogleEnvironment = () => {
  if (typeof window === 'undefined') return false;
  // Must be running inside a real Android WebView (WTN shell sets the "; wv)" UA flag).
  // Plain Chrome Android, Safari, and desktop browsers all fail this check and fall
  // through to the web GIS button instead.
  if (!isAndroidWebView) return false;
  // WTN v2 injected API (window.WTN.socialLogin.google)
  if (typeof window.WTN?.socialLogin?.google?.login === 'function') return true;
  // WTN v1 native bridge — only if the Social Login add-on is installed
  if (typeof window.WebToNativeInterface?.signInWithGoogle === 'function') return true;
  return false;
};

export async function getGoogleChallenge(platform) {
  const { data } = await axios.get(`${API}/auth/google/challenge`, { params: { platform } });
  return data;
}

async function getNativeGoogleBridge() {
  // WTN v2: window.WTN.socialLogin.google is directly injected by the native shell
  if (typeof window.WTN?.socialLogin?.google?.login === 'function') {
    return window.WTN.socialLogin.google;
  }
  // WTN v1: confirm signInWithGoogle is still present (race-condition safety check)
  if (typeof window.WebToNativeInterface?.signInWithGoogle !== 'function') {
    throw new Error('Google sign-in is not supported in this version of the app. Please update the app or use email sign-in.');
  }
  // Load the WTN npm package which wraps window.WebToNativeInterface
  const { login, logout } = await import('webtonative/SocialLogin/google');
  return { login, logout };
}

export async function nativeGoogleSignIn(signal) {
  const bridge = await getNativeGoogleBridge();
  return new Promise((resolve, reject) => {
    let finished = false;
    // Fire-and-forget diagnostic log so we can see EXACTLY what WTN sent
    // back when a user reports a stall. Never blocks sign-in on failure.
    const diag = (stage, payload = {}) => {
      try {
        const body = { stage, ...payload, ts: new Date().toISOString() };
        axios.post(`${API}/auth/google/native-diagnostic`, body, { timeout: 4000 }).catch(() => {});
      } catch { /* ignore */ }
    };
    const settle = (error, token) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      diag(error ? 'error' : 'success', { error: error?.message });
      error ? reject(error) : resolve(token);
    };
    const cancel = () => settle(new Error('Google sign-in cancelled.'));
    // 45s instead of 90s: half a minute past the point the user has actually
    // seen the account chooser is plenty; anything longer just leaves them
    // staring at a spinner instead of falling back to email sign-in.
    const timer = setTimeout(() => settle(new Error('Google did not respond. Please try again or use email sign-in.')), 45000);
    if (signal?.aborted) return cancel();
    signal?.addEventListener('abort', cancel, { once: true });
    diag('bridge_login_invoked', { hasV2: typeof window.WTN?.socialLogin?.google?.login === 'function' });
    try {
      bridge.login({ callback: value => {
        // Log the raw response shape (without the token itself) so we can
        // diagnose future stalls even after they clear the app cache.
        const shape = {
          keys: value && typeof value === 'object' ? Object.keys(value).slice(0, 20) : [],
          isSuccess: value?.isSuccess,
          success: value?.success,
          type: value?.type,
          hasIdToken: !!(value?.idToken || value?.id_token),
          hasToken: !!value?.token,
          error: value?.error,
        };
        diag('callback', shape);
        // Accept every reasonable success-flag variant WTN builds have emitted:
        //   { isSuccess: true }              (docs / current)
        //   { isSuccess: 'true' }            (some Android builds serialise it)
        //   { success: true }                (older plugin fork)
        //   type === 'googleLoginToken' + a valid token
        const positive = value?.isSuccess === true || value?.isSuccess === 'true'
                       || value?.success === true;
        const token = value?.idToken || value?.id_token || value?.token;
        if ((positive || value?.type === 'googleLoginToken') && typeof token === 'string' && token.length > 20) {
          settle(null, token);
        } else {
          const cancelled = /cancel|12501/i.test(String(value?.error || ''));
          settle(new Error(cancelled ? 'Google sign-in cancelled.' : 'Google sign-in failed. Please try again or use email sign-in.'));
        }
      } });
    } catch (err) {
      diag('bridge_login_threw', { error: err?.message });
      settle(new Error('Google sign-in is not supported in this version of the app. Please update the app or use email sign-in.'));
    }
  });
}

export function clearGoogleSession() {
  window.google?.accounts?.id?.disableAutoSelect?.();
  if (isNativeGoogleEnvironment()) {
    getNativeGoogleBridge().then(bridge => bridge.logout?.({ callback: () => {} })).catch(() => {});
  }
}